import React, { useEffect, useMemo, useState } from 'react';

import TwAutocomplete, {
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import TwWarning from '../../../components/TwWarning/TwWarning';
import {
  Characters,
  useAllowCharacters,
  useUnformatValue
} from '../../../hooks/useAllowCharacters';

import useAuth from '../../../providers/Auth/Auth.provider';

import StringUtils from '../../../utils/stringUtils';
import CreateEquipment from '../../Equipment/CreateEquipment/CreateEquipment';

import './EditDriver.scss';
import { CarrierResponse } from '../../../redux/api/carriers/carriers.model';
import { useGetCarriersQuery } from '../../../redux/api/carriers/carrierApi';
import {
  CreateDriverData,
  DRIVER_DEFAULT_PAY_RATE_TYPE,
  DRIVER_TYPE
} from '../../../redux/api/driver/driver.model';
import {
  useGetDriverByIdQuery,
  useUpdateDriverMutation
} from '../../../redux/api/driver/driverApi';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { useSearchEquipmentsQuery } from '../../../redux/api/equipment/equipmentApi';
import { Equipment, EquipmentType } from '../../../redux/api/equipment/equipment.model';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import TwCheckbox from '../../../components/TwCheckboxes/TwCheckboxes';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import TwCompaniesModal from '../../Companies/CompaniesModal/TwCompaniesModal';
import { CompaniesEntities } from '../../Companies/Companies';
import { TRIP_DRIVER_PAY_UNIT } from '../../../redux/api/trips/trips.model';
import TwDriverPayRateTypeSelector from '../../../components/TwDriverPayRateTypeSelector/TwDriverPayRateTypeSelector';
import {
  useGetConnectionsQuery,
  useGetDriversByConnectionQuery
} from '../../../redux/api/eld/eldApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetFuelCardsQuery } from '../../../redux/api/fuelcard/fuelcardApi';
import FuelcardUtils from '../../../utils/fuelcardUtils';
import useCheckDriverPay from '../../../hooks/useCheckDriverPay';
import { EntityType } from '../../../redux/Global.model';
import { updateDriverPayConfirmationModal } from '../../../redux/features/orders/ordersSlice';
import { updateTripLtlDriverPayConfirmationModal } from '../../../redux/features/trip/tripSlice';
import { useAppDispatch } from '../../../redux/hooks';

interface EditDriverFormValues {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  companyName?: string;
  defaultTruck?: TwAutocompleteOption | null;
  defaultTrailer?: TwAutocompleteOption | null;
  defaultPayRateType?: TwAutocompleteOption | null;
  payRate?: string;
  type?: TwAutocompleteOption | null;
  carrier?: TwAutocompleteOption | null;
  deductFuelPurchases?: boolean;
  eldConnectionId?: TwAutocompleteOption | null;
  eldData?: TwAutocompleteOption | null;
  fuelCard?: TwAutocompleteOption | null;
  clearDriverPay: boolean;
}

const initialValues: EditDriverFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  defaultTruck: null,
  defaultTrailer: null,
  defaultPayRateType: null,
  payRate: '',
  type: null,
  carrier: null,
  deductFuelPurchases: false,
  eldConnectionId: null,
  eldData: null,
  fuelCard: null,
  clearDriverPay: false
};

export interface EditDriverProps {
  open: boolean;
  driverId: string;
  onClose: () => void;
  onSuccess?: (
    driverId: string,
    driver?: CreateDriverData,
    selectedTruck?: Equipment,
    selectedCarrier?: CarrierResponse,
    selectedTrailer?: Equipment,
    clearDriverPay?: boolean
  ) => Promise<void>;
  onCarrierCreated?: (newCarrier: CarrierResponse) => void;
  onEquipmentCreated?: (newEquipment: Equipment) => void;
  entity?: EntityType;
}

const EditDriver: React.FC<EditDriverProps> = ({
  open,
  driverId,
  onCarrierCreated,
  onClose,
  onEquipmentCreated,
  onSuccess,
  entity
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { addNotification } = useNotifications();
  const { enableFuelTransactions, enableLiveTracking, enableFuelcardIntegration } =
    useFeatureFlags();

  const dispatch = useAppDispatch();
  const anyDriverPayExists = useCheckDriverPay({ entity: entity || null });

  // For lists
  const [carriers, setCarriers] = useState<TwAutocompleteOption[]>([]);
  const [loadCarriers, setLoadCarriers] = useState<CarrierResponse[]>([]);
  const [loadEquipment, setLoadEquipment] = useState<Equipment[]>([]);
  const [trucks, setTrucks] = useState<TwAutocompleteOption[]>([]);
  const [trailers, setTrailers] = useState<TwAutocompleteOption[]>([]);
  const [types, setTypes] = useState<TwAutocompleteOption[]>([]);

  const [formData, setFormData] = useState<EditDriverFormValues>(initialValues);

  const [createCarrierDialogOpen, setCreateCarrierDialogOpen] = useState(false);
  const [createEquipmentDialogOpen, setCreateEquipmentDialogOpen] = useState(false);
  const [createEquipmentType, setCreateEquipmentType] = useState<EquipmentType | undefined>(
    undefined
  );

  const [formExpanded, setFormExpanded] = useState<boolean>(false);
  const [eldFormExpanded, setEldFormExpanded] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  const [connectionsOptions, setConnectionsOptions] = useState<TwAutocompleteOption[]>([]);
  const [eldDriversOptions, setEldDriversOptions] = useState<TwAutocompleteOption[]>([]);
  const [selectedEldIntegration, setSelectedEldIntegration] = useState<string | null>(null);

  const unformatString = useUnformatValue(Characters.number);
  const handleKeyDownCharacters = useAllowCharacters(Characters.phoneNumber);
  const { data: carriersList = { content: [], total: 0 } } = useGetCarriersQuery({});
  const { data: equipmentList = { equipments: [], total: 0 } } = useSearchEquipmentsQuery({});
  const { data: driver } = useGetDriverByIdQuery(driverId, {
    skip: !driverId
  });
  const { data: connections = { content: [], total: 0 } } = useGetConnectionsQuery({});
  const { data: eldDrivers = { content: [], total: 0 }, isError: hasEldDriversError } =
    useGetDriversByConnectionQuery(
      selectedEldIntegration
        ? {
            connectionId: selectedEldIntegration
          }
        : skipToken
    );
  const [updateDriver] = useUpdateDriverMutation();

  const { data: fuelCards, isLoading: isFuelCardsLoading } = useGetFuelCardsQuery(
    enableFuelcardIntegration ? undefined : skipToken,
    {
      selectFromResult: ({ data, isLoading }) => ({
        data: data?.cards?.map((c) => FuelcardUtils.mapFuelcardToAutocompleteOption(c)) || [],
        isLoading
      })
    }
  );

  useEffect(() => {
    setFormExpanded(false);
  }, []);

  useEffect(() => {
    if (connections.content && connections.content.length > 0) {
      const options = connections.content.map((connection) => ({
        id: connection.id,
        label: connection.name,
        value: connection.id
      }));

      setConnectionsOptions(options);
      return;
    }

    setConnectionsOptions([]);
  }, [connections]);

  useEffect(() => {
    if (hasEldDriversError) {
      setEldDriversOptions([]);
      return;
    }

    if (eldDrivers.content && eldDrivers.content.length > 0) {
      const options = eldDrivers.content.map((eldDriver) => ({
        id: eldDriver.eldDriverId,
        label: `${eldDriver.firstName} ${eldDriver.lastName}`,
        value: eldDriver.eldDriverId
      }));

      setEldDriversOptions(options);
      return;
    }

    setEldDriversOptions([]);
  }, [JSON.stringify(eldDrivers), hasEldDriversError]);

  const [selectedDefaultPayRateType, setSelectedDefaultPayRateType] = useState<
    DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT | null
  >(null);

  const payRateStartAdornment = useMemo<string | undefined>(
    () =>
      selectedDefaultPayRateType &&
      selectedDefaultPayRateType !== DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE
        ? '$'
        : undefined,
    [selectedDefaultPayRateType]
  );

  const payRateEndAdornment = useMemo<string | undefined>(
    () =>
      selectedDefaultPayRateType &&
      selectedDefaultPayRateType == DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE
        ? '%'
        : undefined,
    [selectedDefaultPayRateType]
  );

  useEffect(() => {
    const newTypes: TwAutocompleteOption[] = [
      {
        id: DRIVER_TYPE.CONTRACTOR,
        label: StringUtils.getDriverTypeLabel(DRIVER_TYPE.CONTRACTOR),
        value: DRIVER_TYPE.CONTRACTOR
      },
      {
        id: DRIVER_TYPE.EMPLOYEE,
        label: StringUtils.getDriverTypeLabel(DRIVER_TYPE.EMPLOYEE),
        value: DRIVER_TYPE.EMPLOYEE
      }
    ];
    const newPayRateTypes: TwAutocompleteOption[] = [
      {
        id: DRIVER_DEFAULT_PAY_RATE_TYPE.PER_HOUR,
        label: StringUtils.getDriverPayRateTypeLabel(DRIVER_DEFAULT_PAY_RATE_TYPE.PER_HOUR),
        value: DRIVER_DEFAULT_PAY_RATE_TYPE.PER_HOUR
      },
      {
        id: DRIVER_DEFAULT_PAY_RATE_TYPE.PER_MILE,
        label: StringUtils.getDriverPayRateTypeLabel(DRIVER_DEFAULT_PAY_RATE_TYPE.PER_MILE),
        value: DRIVER_DEFAULT_PAY_RATE_TYPE.PER_MILE
      }
    ];

    if (!enableLtl) {
      newPayRateTypes.push({
        id: DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE,
        label: StringUtils.getDriverPayRateTypeLabel(DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE),
        value: DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE
      } as TwAutocompleteOption);
    }

    setTypes(newTypes);
  }, []);

  useEffect(() => {
    if (!open || !driver) return;

    // (async (): Promise<void> => {
    //   try {
    const truckEquipment =
      equipmentList &&
      equipmentList.equipments &&
      equipmentList.equipments.filter((t) => t.type === EquipmentType.truck);
    const trailerEquipment =
      equipmentList &&
      equipmentList.equipments &&
      equipmentList.equipments.filter((t) => t.type === EquipmentType.trailer);

    truckEquipment &&
      setTrucks(truckEquipment.map((e) => ({ id: e.id, label: e.name, value: e.id })));
    equipmentList && setLoadEquipment(equipmentList.equipments);
    trailerEquipment &&
      setTrailers(trailerEquipment.map((e) => ({ id: e.id, label: e.name, value: e.id })));
    carriersList &&
      setCarriers(carriersList.content.map((c) => ({ id: c.id, label: c.name, value: c.id })));
    carriersList && setLoadCarriers(carriersList.content as CarrierResponse[]);

    if (driver) {
      const carrier =
        carriersList &&
        (carriersList.content as CarrierResponse[]).find((c) => c.id === driver.carrier?.id);
      const newDefaultTruck =
        equipmentList &&
        equipmentList.equipments &&
        equipmentList.equipments.find((e) => e.id === driver.default_truck?.id);
      const newDefaultTrailer =
        equipmentList &&
        equipmentList.equipments &&
        equipmentList.equipments.find((e) => e.id === driver.default_trailer?.id);

      setFormData({
        ...formData,
        firstName: driver.first_name || '',
        lastName: driver.last_name || '',
        companyName: driver.company_name?.toString() || '',
        email: driver.email_address || '',
        phone: StringUtils.formatPhoneNumber(driver.phone?.toString() || ''),
        defaultTruck: newDefaultTruck
          ? { id: newDefaultTruck.id, label: newDefaultTruck.name, value: newDefaultTruck.id }
          : null,
        defaultTrailer: newDefaultTrailer
          ? {
              id: newDefaultTrailer.id,
              label: newDefaultTrailer.name,
              value: newDefaultTrailer.id
            }
          : null,
        defaultPayRateType: driver.default_pay_rate_type
          ? {
              id: driver.default_pay_rate_type,
              label: StringUtils.getDriverPayRateTypeLabel(driver.default_pay_rate_type),
              value: driver.default_pay_rate_type
            }
          : null,
        payRate: driver.pay_rate || '',
        type: driver.type
          ? {
              id: driver.type,
              label: StringUtils.getDriverTypeLabel(driver.type),
              value: driver.type
            }
          : null,
        carrier: carrier ? { id: carrier.id, label: carrier.name, value: carrier.id } : null,
        eldConnectionId:
          enableLiveTracking && !formData.eldConnectionId
            ? driver.eld_connection_id
              ? connectionsOptions.find((c) => c.id === driver.eld_connection_id) || null
              : null
            : formData.eldConnectionId
            ? formData.eldConnectionId
            : null,
        eldData:
          enableLiveTracking && !formData.eldData
            ? driver.eld_data
              ? {
                  id: driver.eld_data.eldDriverId,
                  label: `${driver.eld_data.firstName} ${driver.eld_data.lastName}`,
                  value: driver.eld_data.eldDriverId
                }
              : null
            : formData.eldData
            ? formData.eldData
            : null,
        deductFuelPurchases: !!driver.deduct_fuel_purchase_on_settlement,
        fuelCard: driver.fuel_card
          ? {
              id: driver.fuel_card.id,
              label: FuelcardUtils.generateFuelCardOptionLabel(driver.fuel_card),
              value: driver.fuel_card.id
            }
          : null
      });

      setSelectedDefaultPayRateType(driver.default_pay_rate_type || null);
      !enableLiveTracking && setEldFormExpanded(false);
      enableLiveTracking && driver.eld_connection_id && setEldFormExpanded(true);
      enableLiveTracking &&
        driver.eld_connection_id &&
        setSelectedEldIntegration(driver.eld_connection_id);
    }
    //   } catch (error) {
    //     console.log(error);
    //   }
    // })();
  }, [
    open,
    driver,
    connectionsOptions,
    enableLiveTracking,
    JSON.stringify(carriersList.content),
    JSON.stringify(equipmentList.equipments)
  ]);

  const clearDriverData = (): void => {
    setFormData(initialValues);
  };

  /** Submits the new information for the driver */
  const handleSubmit = (): void => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      logRocketTrack('Driver Management', {
        action: 'Edit driver',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'You must fill "First name" and "Last name"',
        variant: 'error',
        position: 'bottomRight',
        timeToShow: 3000
      });
      return;
    }

    const data: CreateDriverData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      companyName: formData.companyName,
      email: formData.email?.trim(),
      phone: formData.phone ? unformatString(formData.phone) : undefined,
      defaultTruckId: (formData.defaultTruck && formData.defaultTruck.id) || undefined,
      defaultTrailerId: (formData.defaultTrailer && formData.defaultTrailer.id) || undefined,
      defaultPayRateType:
        (formData.defaultPayRateType &&
          (formData.defaultPayRateType.id as DRIVER_DEFAULT_PAY_RATE_TYPE)) ||
        undefined,
      payRate: formData.payRate,
      carrierId: (formData.carrier && formData.carrier.id) || undefined,
      type: (formData.type && (formData.type.id as DRIVER_TYPE)) || undefined,
      deductFuelPurchaseOnSettlement: !!formData.deductFuelPurchases,
      eldConnectionId: formData.eldConnectionId
        ? (formData.eldConnectionId.value as string)
        : undefined,
      eldData: formData.eldData
        ? eldDrivers.content.find((d) => d.eldDriverId === formData.eldData?.id) || undefined
        : undefined,
      fuelCardId: (formData.fuelCard && formData.fuelCard.id) || undefined
    };

    updateDriver({
      id: driverId,
      data
    })
      .unwrap()
      .then(async () => {
        const newTruck = loadEquipment.find((e) => e.id === data.defaultTruckId);
        const newTrailer = loadEquipment.find((e) => e.id === data.defaultTrailerId);
        const newCarrier = loadCarriers.find((c) => c.id === data.carrierId);

        if (onSuccess) {
          // Adding timeout so rtk has time to update the drivers list
          // this is useful for the load build flow, when updating drivers and clearing estimated pay
          setTimeout(async () => {
            await onSuccess(
              driverId,
              data,
              newTruck,
              newCarrier,
              newTrailer,
              formData.clearDriverPay
            );
            handleClose();
          }, 1000);
        } else {
          handleClose();
        }
      })
      .catch((error) => {
        addNotification({
          message: error.message,
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
      });
  };

  const handleClose = (): void => {
    setFormExpanded(false);
    setEldFormExpanded(false);
    clearDriverData();
    onClose();
  };

  const addLinkStyle: React.CSSProperties = {
    color: '#3A4DE9',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '18px',
    letterSpacing: '0px',
    textAlign: 'left',
    cursor: 'pointer',
    marginRight: '140px',
    marginTop: '-70px'
  };

  const showELDForm = useMemo((): boolean => {
    return enableLiveTracking && connections && connections.total > 0;
  }, [enableLiveTracking, connections]);

  const payRateUpdated = useMemo(() => {
    if (!driver) return false;
    let payRateTypeChanged = false;
    if (formData.defaultPayRateType === null && driver.default_pay_rate_type !== null) {
      payRateTypeChanged = true;
    } else if (formData.defaultPayRateType !== null && driver.default_pay_rate_type === null) {
      payRateTypeChanged = true;
    } else if (
      formData.defaultPayRateType &&
      formData.defaultPayRateType.id !== driver.default_pay_rate_type
    ) {
      payRateTypeChanged = true;
    }

    let payRateChanged = false;
    if (driver.pay_rate === null && formData.payRate !== '') {
      payRateChanged = true;
    } else if (driver.pay_rate !== null && formData.payRate === '') {
      payRateChanged = true;
    } else if (
      driver.pay_rate !== null &&
      formData.payRate !== null &&
      driver.pay_rate !== formData.payRate
    ) {
      payRateChanged = true;
    }

    return payRateTypeChanged || payRateChanged;
  }, [driver, formData.defaultPayRateType, formData.payRate]);

  const DialogActions = useMemo((): JSX.Element[] => {
    return [
      <div key="0" style={addLinkStyle}>
        <div
          key="1"
          style={{
            display: formExpanded ? 'none' : 'block'
          }}
          onClick={(): void => setFormExpanded(true)}
        >
          + Add equipment and pay info
        </div>{' '}
        {showELDForm && (
          <div
            key="2"
            style={{
              display: eldFormExpanded ? 'none' : 'block'
            }}
            onClick={(): void => setEldFormExpanded(true)}
          >
            + Add ELD connection
          </div>
        )}
      </div>,
      <TwButton
        key="3"
        fullwidth={false}
        type={TwButtonType.secondary}
        label="Cancel"
        action={(): void => {
          handleClose();
          setFormExpanded(false);
        }}
      />,
      <TwButton
        key="4"
        fullwidth={false}
        type={TwButtonType.primary}
        label="Save"
        action={(): void => {
          if (anyDriverPayExists && entity && payRateUpdated) {
            setFormData({ ...formData, clearDriverPay: true });
            if (enableLtl && entity === EntityType.TRIP) {
              dispatch(
                updateTripLtlDriverPayConfirmationModal({
                  open: true,
                  action: () => handleSubmit()
                })
              );
            } else {
              dispatch(
                updateDriverPayConfirmationModal({
                  open: true,
                  action: () => handleSubmit()
                })
              );
            }
          } else {
            setFormData({ ...formData, clearDriverPay: false });
            handleSubmit();
          }
        }}
      />
    ];
  }, [
    enableLiveTracking,
    eldFormExpanded,
    formExpanded,
    formData,
    connections,
    anyDriverPayExists,
    entity
  ]);

  const handleCarrierCreated = (newCarrier: CarrierResponse): void => {
    const newAutocompleteOption: TwAutocompleteOption = {
      id: newCarrier.id,
      label: newCarrier.company?.name || '',
      value: newCarrier
    };
    const newCarriers = [...carriers, newAutocompleteOption];
    const newLoadCarriers = [...loadCarriers, newCarrier];

    setCarriers(newCarriers);
    setLoadCarriers(newLoadCarriers);

    onCarrierCreated && onCarrierCreated(newCarrier);

    setTimeout(() => {
      setFormData({ ...formData, carrier: newAutocompleteOption });
    }, 250);
  };

  const handleEquipmentCreated = (newEquipment: Equipment): void => {
    const newAutocompleteOption: TwAutocompleteOption = {
      id: newEquipment.id,
      label: newEquipment.name,
      value: newEquipment
    };
    const newTrucks = [...trucks, newAutocompleteOption];
    const newLoadEquipment = [...loadEquipment, newEquipment];

    setTrucks(newTrucks);
    setLoadEquipment(newLoadEquipment);

    onEquipmentCreated && onEquipmentCreated(newEquipment);

    switch (newEquipment.type) {
      case EquipmentType.truck:
        setFormData({ ...formData, defaultTruck: newAutocompleteOption });
        break;
      case EquipmentType.trailer:
        setFormData({ ...formData, defaultTrailer: newAutocompleteOption });
        break;
      default:
        return;
    }
  };

  const entityType = 'driver';

  return (
    <>
      <TwDialog
        open={open && !showWarning}
        onClickClose={(): void => {
          handleClose();
          setFormExpanded(false);
        }}
        size="sm"
        title="Edit driver"
        actions={DialogActions}
        draggable={true}
      >
        <div className="edit-driver__wrapper tid-edit-driver__wrapper">
          <div className="edit-driver__form-section">
            <div className="edit-driver__inputs-container">
              <div>
                <div className="edit-driver__two-column">
                  <div
                    className="tid-edit-driver__firstname
                  edit-driver__two-column-item"
                  >
                    <TwField
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      label="First name"
                      inputType={TwInputTypes.text}
                      disabled={false}
                      onInputChange={(e): void => {
                        setFormData({ ...formData, firstName: e.target.value });
                      }}
                      error={false}
                    />
                  </div>

                  <div
                    className="tid-edit-driver__lastname
                  edit-driver__two-column-item"
                  >
                    <TwField
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      label="Last name"
                      inputType={TwInputTypes.text}
                      onInputChange={(e): void => {
                        setFormData({ ...formData, lastName: e.target.value });
                      }}
                      disabled={false}
                      error={false}
                    />
                  </div>
                </div>

                <div className="edit-driver__two-column">
                  <div
                    className="tid-edit-driver__phone
                  edit-driver__two-column-item"
                  >
                    <TwField
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      label="Phone number (optional)"
                      placeholder="(___) ___-____"
                      inputType={TwInputTypes.text}
                      disabled={false}
                      onInputChange={(e): void => {
                        setFormData({ ...formData, phone: e.target.value });
                      }}
                      error={false}
                      onKeyDown={handleKeyDownCharacters}
                    />
                  </div>
                  <div
                    className="tid-edit-driver__email
                  edit-driver__two-column-item"
                  >
                    <TwField
                      id="email"
                      name="email"
                      value={formData.email}
                      label="Email (optional)"
                      inputType={TwInputTypes.text}
                      disabled={false}
                      onInputChange={(e): void => {
                        setFormData({ ...formData, email: e.target.value });
                      }}
                      error={false}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: formExpanded ? 'block' : 'none' }}>
                {userData?.tenantSettings && userData.tenantSettings['advanced-driver-fields'] && (
                  <>
                    <label className="edit-driver__sub-title">Default equipment</label>

                    <div className="edit-driver__two-column">
                      <div
                        className="tid-edit-driver__truck
                          edit-driver__two-column-item"
                      >
                        <TwAutocomplete
                          id="truck"
                          name="truck"
                          label="Truck"
                          optionList={trucks}
                          value={formData.defaultTruck}
                          onChange={(_, newValue: TwAutocompleteOption | null): void => {
                            setFormData({ ...formData, defaultTruck: newValue });
                          }}
                          listAction={{
                            label: 'New truck',
                            action: (): void => {
                              setCreateEquipmentType(EquipmentType.truck);
                              setCreateEquipmentDialogOpen(true);
                            }
                          }}
                        />
                      </div>
                      <div
                        className="tid-create-driver__trailer
                          create-driver__two-column-item"
                      >
                        <TwAutocomplete
                          id="trailer"
                          name="trailer"
                          label="Trailer"
                          optionList={trailers}
                          value={formData.defaultTrailer}
                          onChange={(_, newValue: TwAutocompleteOption | null): void => {
                            setFormData({ ...formData, defaultTrailer: newValue });
                          }}
                          listAction={{
                            label: 'New trailer',
                            action: (): void => {
                              setCreateEquipmentType(EquipmentType.trailer);
                              setCreateEquipmentDialogOpen(true);
                            }
                          }}
                        />
                      </div>
                    </div>

                    <label className="edit-driver__sub-title">Settlement info</label>

                    <div className="edit-driver__two-column">
                      <div
                        className="tid-edit-driver__type
                          edit-driver__two-column-item"
                      >
                        <TwAutocomplete
                          id="type"
                          name="type"
                          label="Type"
                          optionList={types}
                          value={formData.type}
                          onChange={(_, newValue: TwAutocompleteOption | null): void => {
                            setFormData({ ...formData, type: newValue });
                          }}
                        />
                      </div>

                      {userData?.tenantSettings && userData.tenantSettings['multiple-carriers'] && (
                        <div
                          className="tid-edit-driver__carrier
                          edit-driver__two-column-item"
                        >
                          <TwAutocomplete
                            id="carrier"
                            name="carrier"
                            label="Carrier"
                            optionList={carriers}
                            value={formData.carrier}
                            onChange={(_, newValue: TwAutocompleteOption | null): void => {
                              setFormData({ ...formData, carrier: newValue });
                            }}
                            listAction={{
                              label: 'New carrier',
                              action: (): void => setCreateCarrierDialogOpen(true)
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="edit-driver__two-column">
                      <div
                        className="tid-edit-driver__pay-rate-type
                          edit-driver__two-column-item"
                      >
                        <TwDriverPayRateTypeSelector
                          id="pay-rate-type"
                          name="pay-rate-type"
                          label="Pay rate type"
                          value={formData.defaultPayRateType}
                          onChange={(_, newValue: TwAutocompleteOption | null): void => {
                            setSelectedDefaultPayRateType(
                              newValue ? (newValue.id as DRIVER_DEFAULT_PAY_RATE_TYPE) : null
                            );
                            setFormData({ ...formData, defaultPayRateType: newValue });
                          }}
                        />
                      </div>
                      <div
                        className="tid-edit-driver__pay-rate
                          edit-driver__two-column-item"
                      >
                        <TwField
                          id="payRate"
                          name="payRate"
                          value={formData.payRate}
                          label="Rate"
                          inputType={TwInputTypes.number}
                          onInputChange={(e): void =>
                            setFormData({ ...formData, payRate: e.target.value })
                          }
                          error={false}
                          startAdorment={payRateStartAdornment}
                          endAdorment={payRateEndAdornment}
                        />
                      </div>
                    </div>

                    {enableFuelcardIntegration && (
                      <div className="edit-driver__two-column">
                        <div
                          className="tid-edit-driver__pay-rate-type
                          edit-driver__two-column-item"
                        >
                          <TwAutocomplete
                            id="fuelCard"
                            name="fuelCard"
                            label="Fuel card"
                            optionList={fuelCards}
                            value={formData.fuelCard}
                            onChange={(_, newValue: TwAutocompleteOption | null): void => {
                              setFormData({ ...formData, fuelCard: newValue });
                            }}
                            loading={isFuelCardsLoading}
                          />
                        </div>
                      </div>
                    )}

                    {enableFuelTransactions && (
                      <TwCheckbox
                        id="deductFuelPurchases"
                        name="deductFuelPurchases"
                        label="Deduct fuel purchases on driver settlements"
                        value={formData.deductFuelPurchases}
                        checked={formData.deductFuelPurchases}
                        onInputChange={(): void => {
                          setFormData({
                            ...formData,
                            deductFuelPurchases: !formData.deductFuelPurchases
                          });
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              <div style={{ display: eldFormExpanded && showELDForm ? 'block' : 'none' }}>
                {showELDForm && (
                  <>
                    <label className="edit-driver__sub-title">ELD Connection</label>

                    <div className="edit-driver__two-column">
                      <div
                        className="tid-edit-driver__eld-integration
                          edit-driver__two-column-item"
                      >
                        <TwAutocomplete
                          id="eldIntegrationId"
                          name="eldIntegrationId"
                          label="ELD Integration"
                          optionList={connectionsOptions}
                          value={formData.eldConnectionId}
                          onChange={(_, newValue: TwAutocompleteOption | null): void => {
                            setFormData({ ...formData, eldConnectionId: newValue, eldData: null });
                            setSelectedEldIntegration(newValue ? (newValue.value as string) : null);
                          }}
                        />
                      </div>
                      <div
                        className="tid-create-driver__eld-data
                          create-driver__two-column-item"
                      >
                        <TwAutocomplete
                          id="eldData"
                          name="eldData"
                          label="ELD connected driver"
                          optionList={eldDriversOptions}
                          value={formData.eldData}
                          onChange={(_, newValue: TwAutocompleteOption | null): void => {
                            setFormData({ ...formData, eldData: newValue });
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </TwDialog>

      <TwCompaniesModal
        open={createCarrierDialogOpen}
        onClose={(): void => setCreateCarrierDialogOpen(false)}
        onSuccess={handleCarrierCreated}
        entity={CompaniesEntities.carrier}
      />
      <CreateEquipment
        open={createEquipmentDialogOpen}
        defaultType={createEquipmentType}
        onClose={(): void => setCreateEquipmentDialogOpen(false)}
        onSuccess={handleEquipmentCreated}
      />
      <TwWarning
        warningTitle={`Editing ${entityType} on all orders`}
        warningMessage={`By editing this ${entityType} (${formData.firstName} ${formData.lastName}), you will be editing the ${entityType} on all orders with it assigned. Do you want to
      continue?`}
        open={open}
        handleClose={handleClose}
        showWarning={showWarning}
        setShowWarning={setShowWarning}
        reloadId={driverId}
      ></TwWarning>
    </>
  );
};

export default EditDriver;
